// Catalog.ID Client — Registration entry page (/register)
// ============================================================================
// The anonymous front door (registration-restructure §14.2). One "enter your
// code" field, a scan control beside it, a link to the storefront, and — for
// somebody arriving with no code at all — the two registration flows named
// explicitly.
//
// WHY a code field is the front door. An invitation is now a 16-character code
// (§14.2) and a voucher has always been one; both are things people are handed
// on paper, read over a telephone, or receive in a letter. The prefixes are
// distinct (`INV-` vs `RGV-`), so the KIND of code is decided here with no round
// trip and nothing to leak: a mistyped code never reaches the node.
//
//   RGV-…  → one round trip to `inspect` for the code's type, then that
//            wizard with the code, which its payment step confirms rather
//            than asks for again (checked-voucher-step.ts).
//   INV-…  → one round trip to `inspect` (§14.3), because since M19 an
//            invitation can offer a personal registration, an organization
//            registration or a delegate seat, and those are three wizards. The
//            lookup id that round trip needs is derived from the code here and
//            handed on, so the scrypt is paid for once.
//
// The SCAN control decodes a QR into the entry URL and reads the code out of
// its fragment — a second carrier of the same code and nothing else, for laptop
// cameras and for Wi-Fi printing from a phone. It is a convenience: the code is
// printed beside it in 12 pt, so a camera that will not start costs nobody
// anything (the desktop camera path is compile-checked only — §14.2 lists a
// functional camera test as a prerequisite before it is relied on).
//
// Everything internal goes through dashboardHref(), so `?dp=` survives inside
// the desktop dashboard (§11).
//
// A DELEGATE invitation lives only at its organization's origin node
// (docs/cid-governance.md M7a), which need not be the node this client entered
// through. Its link carries the organization beside the code (`org=`), and a
// typed code comes with the "Organization ID (printed on the card)" field; the
// organization's origin is looked up and the invitation inspected THERE.
//
// THE REGISTRATION GATE (docs/cid-governance.md M12). A node that registers by
// invitation only — or that could not be asked — gets the code field and nothing
// else: no registration started without a code, no storefront link, and no
// "Personalize it for someone else". The page is painted closed and opened by
// the node's explicit `open` (auth/registration-gate.ts), never the reverse.

import { createElement } from '@catalog-shell';
import { createLiveScanner } from '../components/card-scan';
import {
    normaliseInvitationCode, normaliseOrgCid, formatInvitationCode, parseInvitationUrl, deriveInvitationSecrets,
} from './invitation-document';
import { organizationOrigin } from './delegate-activation-ref';
import { normalizeVoucherCode, deriveVoucherSecrets, formatVoucherCode } from './voucher-code';
import { inspectInvitation, type InvitationRegistrationType } from '../api/invitation';
import { inspectVoucher, type VoucherInspectResponse } from '../api/voucher';
import { dashboardHref } from '../state/dashboard-context';
import { fetchRegistrationConfig, type RegistrationConfig } from './payment-flow';
import { registrationOffer, type RegistrationOffer } from './registration-gate';

/** What a typed or scanned code turns out to be. */
export type EntryCode =
    | { kind: 'invitation'; code: string; orgCid?: string }
    | { kind: 'voucher'; code: string };

/**
 * Pure: classify what somebody typed. Null when it is neither kind, which is
 * the only thing this page ever refuses — everything else is the node's job.
 *
 * Both normalisers fold case, separators and the Crockford look-alikes, so a
 * code read off paper in any grouping lands on the same canonical string.
 */
export function classifyEntryCode(raw: string | null | undefined): EntryCode | null {
    const invitation = normaliseInvitationCode(raw);
    if (invitation) return { kind: 'invitation', code: invitation };
    const voucher = normalizeVoucherCode(raw);
    if (voucher) return { kind: 'voucher', code: voucher };
    return null;
}

/**
 * Pure: does the code field hold an invitation code, or the start of one? Only
 * a delegate invitation needs the Organization ID field, and only an `INV-` code
 * can be one, so the field appears with the prefix: the same test, look-alike
 * included, that {@link normaliseInvitationCode} applies.
 */
export function looksLikeInvitationCode(raw: string): boolean {
    const compact = raw.replace(/[\s\-_]/g, '').toUpperCase();
    return compact.startsWith('INV') || compact.startsWith('1NV');
}

/**
 * Pure: read a code out of whatever a QR turned out to contain. The card's QR
 * carries the entry URL, so that is tried first; a QR that carries the bare code
 * (a hand-made one, or a future carrier) still works, and so does one carrying a
 * voucher code, since the storefront may well print one some day.
 */
export function entryCodeFromScan(payload: string): EntryCode | null {
    const fromUrl = parseInvitationUrl(payload);
    if (fromUrl) {
        return fromUrl.orgCid
            ? { kind: 'invitation', code: fromUrl.code, orgCid: fromUrl.orgCid }
            : { kind: 'invitation', code: fromUrl.code };
    }
    return classifyEntryCode(payload.trim());
}

/** What one round trip to `inspect` says an `INV-` code is for. */
export interface InvitationRoute {
    registrationType: InvitationRegistrationType;
    /** The domain an organization invitation fixes, for the wizard's Domain
     *  step. Public — see the note on `inspect`. */
    domain: string | null;
    /** A delegate invitation's organization — the one the card named, or the
     *  one the node answered with. Absent for the other two kinds. */
    orgCid?: string;
}

/** The calls {@link resolveInvitationRoute} makes, injectable for tests. */
export interface InvitationRouteDeps {
    derive: typeof deriveInvitationSecrets;
    inspect: typeof inspectInvitation;
    /** Where a named organization's invitations live (M7a). */
    organizationOrigin?: typeof organizationOrigin;
}

export const defaultInvitationRouteDeps: InvitationRouteDeps = {
    derive: deriveInvitationSecrets,
    inspect: inspectInvitation,
    organizationOrigin,
};

/**
 * Which wizard an `INV-` code belongs to (registration-restructure §14.3).
 *
 * Since M19 an invitation may offer a personal registration, an organization
 * registration or a delegate seat, and those are three different wizards — the
 * prefix alone can no longer say which. So this is the ONE round trip the entry
 * page makes: derive the lookup id from the code (the same scrypt the wizard
 * would run anyway, and its result is handed on so it is not paid for twice),
 * then ask `inspect`, which draws no CID and changes nothing.
 *
 * DOM-free on purpose — the routing decision is the part worth testing.
 */
export async function resolveInvitationRoute(
    code: string, deps: InvitationRouteDeps = defaultInvitationRouteDeps, orgCid?: string,
): Promise<InvitationRoute> {
    const secrets = await deps.derive(code);
    // A card that names its organization is inspected at that organization's
    // origin (M7a): the invitation exists nowhere else.
    const info = orgCid
        ? await deps.inspect(secrets.lookupIdHex,
            (await (deps.organizationOrigin ?? organizationOrigin)(orgCid)).url)
        : await deps.inspect(secrets.lookupIdHex);
    const route: InvitationRoute = { registrationType: info.registrationType, domain: info.domain ?? null };
    const organization = normaliseOrgCid(info.orgCid) ?? orgCid;
    if (organization) route.orgCid = organization;
    return route;
}

// ── the voucher branch (registration-wallets §7, D17, D18) ──────────────────
//
// An `RGV-` code used to go straight to a "personal or organization?" chooser:
// the prefix said "voucher" and only the buyer knew which account they meant.
// It no longer works that way. A voucher is bought AS a type — the three have
// different prices and different Stripe lines — so the ROW says which, and the
// page asks before it offers anything. That one round trip also lets it say
// "this code has already been used" on the spot instead of inside a wizard,
// eight steps from the person holding the card.
//
// Two actions come out of it (D17). **Register with this voucher** is the
// buyer's own use, and it is UNRESTRICTED: fixing your own email before you
// have verified it cannot be undone, so personalization is never a step for
// your own ticket. **Personalize it for someone else** is the act of handing
// the voucher over — it mints an invitation, moves the bits onto it, and is the
// only way a code becomes addressed to one person.
//
// A DELEGATE voucher offers only the second. Delegate stock is universal (D18):
// what binds a delegate to an organization is that organization's own signed
// invitation, issued from its Delegates pane — never the voucher.

/** Why a voucher cannot be used. Each maps to one sentence for its holder. */
export type VoucherUnusableReason = 'expired' | 'consumed' | 'revoked' | 'refunded' | 'unusable';

/** What the entry page offers for one `RGV-` code. */
export type VoucherOffer =
    | {
        kind: 'register-or-personalize';
        registrationType: 'personal' | 'organization';
        /** The row is `reserved`: a registration or an invitation is already
         *  riding on this code. Still offered — see {@link voucherOffer}. */
        inUse: boolean;
    }
    | { kind: 'personalize-only'; registrationType: 'delegate' }
    | { kind: 'unusable'; reason: VoucherUnusableReason; message: string };

const UNUSABLE_MESSAGES: Record<VoucherUnusableReason, string> = {
    expired: 'That registration code has expired. Codes are valid for a limited time; '
        + 'the person who bought it can ask the operator about a replacement.',
    consumed: 'That registration code has already been used — an account was registered with it. '
        + 'If that was not you, ask whoever gave you the code.',
    revoked: 'That registration code was withdrawn and can no longer be used. '
        + 'Ask whoever gave it to you.',
    refunded: 'The payment behind that registration code was refunded, so the code no longer '
        + 'pays for anything.',
    unusable: 'That registration code cannot be used.',
};

/**
 * Pure: what to offer for an inspected voucher.
 *
 * EXPIRY IS FOLDED IN HERE, not on the node (§5): the row carries a date, never
 * an "expired" status, so the node reports and the client judges. The order
 * matters — a CONSUMED voucher whose date has also passed is consumed, not
 * expired, because "it expired" would send its holder to support for a refund
 * they are not owed.
 *
 * A `reserved` row is still offered, flagged. The node accepts `unredeemed` and
 * `reserved` alike (`ResolveRedeemableAsync`): a registration that reserved its
 * own voucher has to be able to resume, and re-begin returns the same CID.
 * Refusing here would strand exactly the person who is mid-flight.
 *
 * Nothing here mentions bits. The node does not know they exist (§1 idea 1) and
 * neither does this page; the bits live on the Worker lock, and the only place
 * they are shown is the card that carries the code.
 */
export function voucherOffer(info: VoucherInspectResponse, now: Date = new Date()): VoucherOffer {
    switch (info.status) {
        case 'consumed':
        case 'revoked':
        case 'refunded':
            return { kind: 'unusable', reason: info.status, message: UNUSABLE_MESSAGES[info.status] };
        case 'unredeemed':
        case 'reserved':
            break;
        default:
            // A status this build does not know. Fail closed: telling somebody a
            // code is fine when it is not sends them into a wizard that refuses
            // them at the end.
            return { kind: 'unusable', reason: 'unusable', message: UNUSABLE_MESSAGES.unusable };
    }

    const expiresAt = Date.parse(info.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt < now.getTime()) {
        return { kind: 'unusable', reason: 'expired', message: UNUSABLE_MESSAGES.expired };
    }

    if (info.registrationType === 'delegate') {
        return { kind: 'personalize-only', registrationType: 'delegate' };
    }
    return {
        kind: 'register-or-personalize',
        registrationType: info.registrationType,
        inUse: info.status === 'reserved',
    };
}

/** The two calls {@link resolveVoucherOffer} makes, injectable for tests. */
export interface VoucherRouteDeps {
    derive: typeof deriveVoucherSecrets;
    inspect: typeof inspectVoucher;
}

export const defaultVoucherRouteDeps: VoucherRouteDeps = {
    derive: deriveVoucherSecrets,
    inspect: inspectVoucher,
};

/**
 * The voucher branch's one round trip: derive the lookup id from the code, ask
 * the node what the row is, fold the answer.
 *
 * The CODE never leaves this device — it is what opens the Worker lock holding
 * the voucher's bits (§4.1), so a node able to see it could open that lock. The
 * lookup id is derived once here and handed back, so the wizard that follows
 * does not pay for the ~0.1 s scrypt a second time.
 */
export async function resolveVoucherOffer(
    code: string,
    deps: VoucherRouteDeps = defaultVoucherRouteDeps,
    now: Date = new Date(),
): Promise<{ offer: VoucherOffer; lookupIdHex: string }> {
    const secrets = await deps.derive(code);
    const info = await deps.inspect(secrets.lookupIdHex);
    return { offer: voucherOffer(info, now), lookupIdHex: secrets.lookupIdHex };
}

export interface RegisterEntryCallbacks {
    /** An `INV-` code, once `inspect` has said what it offers. A `delegate`
     *  invitation goes to the activation wizard; the other two go to the
     *  matching registration wizard with the code in hand. */
    onInvitation(code: string, route: InvitationRoute): void;
    /** Personal registration, with the voucher code this page read, when there is one. */
    onPersonal(voucherCode?: string): void;
    /** Organization registration, same. */
    onOrganization(voucherCode?: string): void;
    /** Back to the login screen. */
    onCancel(): void;
}

export interface RegisterEntryOptions {
    /** A code from the URL fragment (`/register#code=INV-…`). It is read at once,
     *  as if Continue had been pressed: somebody who followed a link or scanned a
     *  QR has already said what they want. */
    initialCode?: string;
    /** The organization a delegate invitation link names (`&org=…`, M7a). */
    initialOrgCid?: string;
    /** Test seam for the invitation round trip. */
    invitationRouteDeps?: InvitationRouteDeps;
    /** Test seam for the voucher round trip. */
    voucherRouteDeps?: VoucherRouteDeps;
    /** Where the registration mode comes from (M12); the node's config by default. */
    registrationConfig?: () => Promise<Pick<RegistrationConfig, 'registrationMode' | 'fromServer'>>;
}

/** Where "Personalize it for someone else" sends the holder: the storefront,
 *  with the code in the FRAGMENT so it reaches no access log and no Referer —
 *  the rule `/verify` and `/register` already follow. */
export function personalizeHref(code: string): string {
    return `${dashboardHref('/vouchers')}#code=${encodeURIComponent(formatVoucherCode(code))}`;
}

/** Show or hide an element. `.cs-btn` and `.cid-auth-field` set `display` in
 *  CSS, which beats the `hidden` attribute, so the inline style does the hiding
 *  and `hidden` keeps the state readable. */
function setShown(el: HTMLElement, shown: boolean): void {
    el.hidden = !shown;
    el.style.display = shown ? '' : 'none';
}

export function renderRegisterEntryView(
    container: HTMLElement,
    callbacks: RegisterEntryCallbacks,
    options: RegisterEntryOptions = {},
): void {
    container.innerHTML = '';

    // The registration gate (M12): asked once, and until it answers the page
    // offers nothing but the code field. A failed read answers closed.
    const gate: Promise<RegistrationOffer> = (options.registrationConfig ?? fetchRegistrationConfig)()
        .then(registrationOffer)
        .catch(() => registrationOffer({ registrationMode: 'invitation', fromServer: false }));

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-auth-card--wide' });

    card.appendChild(createElement('h2', { className: 'cid-auth-title', textContent: 'Join Catalog.ID' }));
    const subtitle = createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'If you were sent an invitation card or given a registration code, enter it here.',
    });
    card.appendChild(subtitle);

    // ── the code field ──────────────────────────────────────────────────────

    const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
    const codeInput = document.createElement('input');
    codeInput.type = 'text';
    codeInput.className = 'cid-auth-input';
    codeInput.placeholder = 'INV-XXXX-XXXX-XXXX-XXXX or RGV-…';
    codeInput.autocomplete = 'off';
    codeInput.spellcheck = false;
    fieldWrapper.appendChild(codeInput);
    card.appendChild(fieldWrapper);

    // The organization a delegate card names (docs/cid-governance.md M7a): its
    // invitations live at the organization's home server, and this is how the
    // page finds it when the code was typed rather than followed as a link.
    // Only delegate cards carry one, so the field appears only for an `INV-` code.
    const orgWrapper = createElement('div', { className: 'cid-auth-field' });
    orgWrapper.appendChild(createElement('label', {
        className: 'cid-text-muted',
        textContent: 'Organization ID (printed on the card) — only if your card shows one',
    }));
    const orgInput = document.createElement('input');
    orgInput.type = 'text';
    orgInput.className = 'cid-auth-input';
    orgInput.placeholder = 'e.g. ab12cd34ef';
    orgInput.autocomplete = 'off';
    orgInput.spellcheck = false;
    orgWrapper.appendChild(orgInput);
    card.appendChild(orgWrapper);
    setShown(orgWrapper, false);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    card.appendChild(errorEl);

    /** Where the voucher branch asks personal-or-organization. */
    const chooserArea = createElement('div');
    card.appendChild(chooserArea);

    const continueBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Continue',
    });
    // THE ONE BUTTON. Continue reads the code; once a registration code has been
    // read and offers a registration, the same button starts it ("Register with
    // this code"). `pendingRegistration` is that offer; editing the code drops it,
    // and the button reads again.
    let pendingRegistration: (() => void) | null = null;

    function primaryAction(): void {
        if (pendingRegistration) pendingRegistration();
        else submit(codeInput.value);
    }

    function resetOffer(): void {
        pendingRegistration = null;
        chooserArea.innerHTML = '';
        continueBtn.textContent = 'Continue';
        setShown(continueBtn, true);
    }

    continueBtn.addEventListener('click', primaryAction);
    codeInput.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); primaryAction(); }
    });
    orgInput.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); primaryAction(); }
    });
    codeInput.addEventListener('input', () => {
        setShown(orgWrapper, looksLikeInvitationCode(codeInput.value));
        // An offer belongs to the code that was read, not to whatever is typed now.
        resetOffer();
        clearError();
    });
    card.appendChild(continueBtn);

    /** The grouped form for the field — what is printed on the card. */
    function displayOf(entry: EntryCode): string {
        return entry.kind === 'invitation'
            ? formatInvitationCode(entry.code) : formatVoucherCode(entry.code);
    }

    function showError(message: string): void {
        errorEl.textContent = message;
        errorEl.classList.add('cid-pane-error--visible');
    }

    function clearError(): void {
        errorEl.classList.remove('cid-pane-error--visible');
    }

    function submit(raw: string): void {
        const entry = classifyEntryCode(raw);
        if (!entry) {
            chooserArea.innerHTML = '';
            showError('That does not look like a Catalog.ID code. Invitation codes start with INV- '
                + 'and registration codes with RGV-.');
            return;
        }
        if (entry.kind === 'invitation' && orgInput.value.trim()) {
            // Checked here, before anything is sent anywhere.
            const orgCid = normaliseOrgCid(orgInput.value);
            if (!orgCid) {
                showError('That Organization ID does not look right. It is the ten letters and digits '
                    + 'printed on your card next to the organization\'s name.');
                return;
            }
            clearError();
            act({ ...entry, orgCid });
            return;
        }
        clearError();
        act(entry);
    }

    function act(entry: EntryCode): void {
        if (entry.kind === 'invitation') {
            setShown(orgWrapper, true);
            if (entry.orgCid) orgInput.value = entry.orgCid;
            void routeInvitation(entry.code, entry.orgCid);
            return;
        }
        setShown(orgWrapper, false);
        codeInput.value = formatVoucherCode(entry.code);
        void routeVoucher(entry.code);
    }

    /**
     * The one round trip. An `INV-` code no longer says by itself which wizard it
     * belongs to (§14.3), so ask — with the button disabled and a line saying
     * what is happening, because the scrypt alone takes about a tenth of a second
     * and the lookup adds a request.
     *
     * A refusal is shown here rather than carried into a wizard: the node's
     * answer ("expired", "revoked", "invalid") is exactly what the holder of the
     * card needs to read.
     */
    async function routeInvitation(code: string, orgCid?: string): Promise<void> {
        continueBtn.setAttribute('disabled', '');
        const previousLabel = continueBtn.textContent;
        continueBtn.textContent = 'Reading your invitation…';
        clearError();
        try {
            const route = await resolveInvitationRoute(code, options.invitationRouteDeps, orgCid);
            scanner.stop();
            callbacks.onInvitation(code, route);
        } catch (err) {
            continueBtn.removeAttribute('disabled');
            continueBtn.textContent = previousLabel;
            const message = err instanceof Error && err.message
                ? err.message
                : 'That invitation could not be read. Check the code printed on your card.';
            // A delegate invitation is found only at its organization's home
            // server; without the Organization ID this page could not ask there.
            showError(orgCid ? message
                : `${message} If your card shows an Organization ID, enter it as well and try again.`);
        }
    }

    /**
     * The voucher branch's round trip (§7, D17). The code's TYPE comes from the
     * node, so there is no chooser any more — the row says what it paid for.
     */
    async function routeVoucher(code: string): Promise<void> {
        resetOffer();
        continueBtn.setAttribute('disabled', '');
        const previousLabel = continueBtn.textContent;
        continueBtn.textContent = 'Reading your code…';
        clearError();
        try {
            const { offer } = await resolveVoucherOffer(code, options.voucherRouteDeps);
            const { personalize } = await gate;
            continueBtn.removeAttribute('disabled');
            continueBtn.textContent = previousLabel;
            renderVoucherOffer(code, offer, personalize);
        } catch {
            continueBtn.removeAttribute('disabled');
            continueBtn.textContent = previousLabel;
            // The node answers the same 401 for a malformed id and an unknown
            // one, deliberately (§5) — so this page must not invent a
            // distinction either.
            showError('We do not recognise that registration code. Check it against the one '
                + 'printed on your voucher.');
        }
    }

    /** @param personalize whether this node takes invitations made from a code —
     *  not under the registration gate (M12). */
    function renderVoucherOffer(code: string, offer: VoucherOffer, personalize: boolean): void {
        resetOffer();

        if (offer.kind === 'unusable') {
            showError(offer.message);
            return;
        }

        if (offer.kind === 'personalize-only') {
            // A delegate seat is not a registration anybody can walk up and use
            // (the same under the registration gate: the organization spends it):
            // it is redeemed through an organization's OWN signed invitation
            // (D18), issued from its Delegates pane. The code is universal stock
            // — any organization may spend it — so the sentence names the pane
            // rather than an organization.
            chooserArea.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'That is a delegate code. It pays for one delegate of an organization, '
                    + 'and it is spent by that organization: open its Delegates pane, choose Invite, '
                    + 'and give this code there. The invitation it produces is what the new delegate '
                    + 'uses to activate.',
            }));
            // Nothing to do on this page: no button, until the code is edited.
            setShown(continueBtn, false);
            return;
        }

        const personal = offer.registrationType === 'personal';
        const register = (): void => {
            scanner.stop();
            if (personal) callbacks.onPersonal(code);
            else callbacks.onOrganization(code);
        };

        // A personal code with nothing to choose and nothing to warn about goes
        // straight into the wizard. An organization code stops here to say a
        // log-in comes first; a code already in use, to say so; and an open node
        // offers "Personalize it for someone else" beside registering.
        if (personal && !offer.inUse && !personalize) {
            register();
            return;
        }

        chooserArea.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: personal
                ? 'That code pays for one personal registration.'
                : 'That code pays for one organization registration.',
        }));
        if (offer.inUse) {
            chooserArea.appendChild(createElement('p', {
                className: 'cid-pane-note',
                textContent: 'A registration or an invitation is already using this code. If that '
                    + 'was you, carry on — you will land back where you left off.',
            }));
        }

        if (personalize) {
            const row = createElement('div', { className: 'cs-pane-toolbar' });
            const handOver = createElement('a', {
                className: 'cs-btn cs-btn--default cs-btn--small',
                textContent: 'Personalize it for someone else',
            });
            handOver.setAttribute('href', personalizeHref(code));
            row.appendChild(handOver);
            chooserArea.appendChild(row);
        }

        // Under the registration gate (M12) a code cannot be turned into an
        // invitation here, so neither note mentions it.
        const note = personal
            ? 'Registering with it here asks for nothing you have not chosen yourself.'
                + (personalize
                    ? ' Personalizing it instead turns it into an invitation addressed to one person — '
                        + 'that is how you hand a code over safely.'
                    : '')
            : 'Registering an organization needs a personal identity first — you will be asked '
                + 'to log in, and your code will be waiting at the payment step.'
                + (personalize
                    ? ' Personalizing it instead turns it into an invitation addressed to one person.'
                    : '');
        chooserArea.appendChild(createElement('p', { className: 'cid-pane-note', textContent: note }));

        pendingRegistration = register;
        continueBtn.textContent = 'Register with this code';
    }

    // ── the scan control ────────────────────────────────────────────────────

    const scanSection = createElement('div', { className: 'cid-recovery-section' });
    scanSection.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Or scan the QR code on your invitation card. Nothing is sent anywhere while '
            + 'you scan — the code is read on this device.',
    }));
    const scanner = createLiveScanner({
        uploadLabel: 'Upload a photo, scan or PDF of the card…',
        onDecoded: (bytes) => {
            let payload = '';
            try {
                payload = new TextDecoder().decode(bytes);
            } catch { /* not text — falls through to the error below */ }
            const entry = entryCodeFromScan(payload);
            if (!entry) {
                showError('That code is not a Catalog.ID invitation. Check the card, or type the '
                    + 'code printed on it.');
                void scanner.resume();
                return;
            }
            clearError();
            codeInput.value = displayOf(entry);
            act(entry);
        },
    });
    // Not started here: the camera is asked for when its button is pressed, so
    // opening the page raises no camera prompt for someone typing a code.
    scanSection.appendChild(scanner.element);
    card.appendChild(scanSection);

    // ── no code: the storefront, and the two flows by name ──────────────────
    // Only on a node that said `open` (M12). Painted empty, filled by the answer.

    const noCode = createElement('div', { className: 'cid-recovery-section' });
    card.appendChild(noCode);
    void gate.then((offer) => {
        if (!offer.openRegistration) {
            noCode.appendChild(createElement('p', {
                className: 'cid-wizard-text', textContent: offer.notice ?? '',
            }));
            return;
        }
        subtitle.textContent = 'If you were sent an invitation card or bought a registration code, enter it '
            + 'here. Otherwise choose how you want to register.';
        noCode.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'No code? Registration is paid for with a registration code, which you can buy '
                + 'here — for yourself or for somebody else.',
        }));
        const buyRow = createElement('div', { className: 'cid-auth-link-row' });
        const buyLink = createElement('a', { className: 'cid-auth-link', textContent: 'Buy a registration code' });
        buyLink.setAttribute('href', dashboardHref('/vouchers'));
        buyRow.appendChild(buyLink);
        noCode.appendChild(buyRow);

        noCode.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'You can also start now and pay inside the wizard.',
        }));
        const startRow = createElement('div', { className: 'cs-pane-toolbar' });
        const personalBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Personal registration',
        });
        personalBtn.addEventListener('click', () => callbacks.onPersonal());
        startRow.appendChild(personalBtn);
        const orgBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Organization registration',
        });
        orgBtn.addEventListener('click', () => callbacks.onOrganization());
        startRow.appendChild(orgBtn);
        noCode.appendChild(startRow);
    });

    const backRow = createElement('div', { className: 'cid-auth-link-row' });
    const backLink = createElement('a', { className: 'cid-auth-link', textContent: 'Back to log in' });
    backLink.setAttribute('href', '#');
    backLink.addEventListener('click', (e) => {
        e.preventDefault();
        scanner.stop();
        callbacks.onCancel();
    });
    backRow.appendChild(backLink);
    card.appendChild(backRow);

    wrapper.appendChild(card);
    container.appendChild(wrapper);

    // ── the fragment ────────────────────────────────────────────────────────
    // Painted first, then acted on: an invitation code navigates away from this
    // page immediately, and a half-built DOM behind it would be visible for the
    // frame it takes.
    if (options.initialCode) {
        const entry = classifyEntryCode(options.initialCode);
        if (entry) {
            codeInput.value = displayOf(entry);
            if (entry.kind === 'invitation') {
                setShown(orgWrapper, true);
                const orgCid = normaliseOrgCid(options.initialOrgCid) ?? undefined;
                if (orgCid) orgInput.value = orgCid;
                void routeInvitation(entry.code, orgCid);
            } else {
                void routeVoucher(entry.code);
            }
        } else {
            codeInput.value = options.initialCode;
            showError('That code was not recognised. Check it against the one printed on your card.');
        }
    }
}
